import React, { useState, useEffect, useContext, useRef } from "react";
import { IoMenu, IoClose, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import { API_BASE } from "../../utils/api";
import { getMyAccountId, isBMI } from "../../utils/config";

export default function MainNavbar() {
  const [open, setOpen] = useState(false);
  const [isShrunk, setIsShrunk] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isProforma, setIsProforma] = useState(
    () => localStorage.getItem("order_mode") === "proforma"
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    const sync = () => setIsProforma(localStorage.getItem("order_mode") === "proforma");
    window.addEventListener("orderModeChange", sync);
    return () => window.removeEventListener("orderModeChange", sync);
  }, []);

  const MYACCOUNT_ID = getMyAccountId();
  const { pathname } = useLocation();
  const { user } = useContext(UserContext);

  const toggleMenu = () => setOpen(!open);

  // Sticky effect
  useEffect(() => {
    const handleScroll = () => setIsShrunk(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cargar categorías leyendo localStorage directo (igual que CategoryPage), no el
  // "user" del contexto: UserProvider arranca en null y lo puebla en un efecto
  // aparte, asi que en cada montaje hay un ciclo de render con user=null antes de
  // tener el dato real — eso hacia que se pidiera la lista generica por error. El
  // dependency array solo sirve para saber CUANDO volver a chequear localStorage
  // (login, cambio de cliente o de sucursal); el valor que se usa siempre es fresco.
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "null");
    let url = `${API_BASE}/inventary-ultra/api/ws/list_categorys?myaccount_id=${MYACCOUNT_ID}`;
    if (isBMI(storedUser)) url += `&id_customer=${storedUser.idCustomer}`;
    fetch(url)
      .then((res) => res.json())
      .then(setCategories)
      .catch((err) => console.error("Error cargando categorías", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MYACCOUNT_ID, user?.idCustomer, user?.idShipTo]);

  // SCROLL DESPACIO (De uno en uno o distancia corta)
  const scroll = (direction) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      // Ajusta este número (200) para que corra más o menos distancia por click
      const scrollAmount = 300;
      const scrollTo = direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

      container.scrollTo({
        left: scrollTo,
        behavior: "smooth" // Esto hace que el movimiento sea fluido y no un salto brusco
      });
    }
  };

  const isActive = (url) =>
    pathname === url ? "text-brand-primary/80 after:scale-x-100" : "text-brand-primary/80";

  return (
    <nav
      className={`sticky top-0 bg-white w-full z-40 border-b border-brand-primary/60 transition-all duration-300 ${isShrunk ? "py-2 shadow-md" : "py-3 shadow"
        }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">

        {/* ================= DESKTOP ================= */}
        <div className="hidden lg:flex items-center flex-1 justify-start gap-2">

          {/* FLECHA IZQUIERDA SIEMPRE VISIBLE */}
          <button
            onClick={() => scroll("left")}
            className="z-10 bg-white border shadow-md rounded-full p-2  hover:bg-brand-primary hover:text-white transition-all active:scale-90 flex-shrink-0"
          >
            <IoChevronBack size={20} />
          </button>

          {/* CONTENEDOR DE CATEGORÍAS (Muestra 5 de inicio) */}
          <div className="relative overflow-hidden" style={{ width: "800px" }}>
            <div
              ref={scrollRef}
              className="flex items-center overflow-x-auto no-scrollbar scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {/* OPCIÓN MANUAL: MI TIENDA */}
              <Link
                to="/categoria/all"
                className={`flex-none w-[160px] text-center px-0 py-0 text-[12px] font-semibold uppercase tracking-tighter transition-all duration-300 hover:text-brand-primary relative
              after:content-[''] after:absolute after:-bottom-1 after:left-1/4 after:h-[2px] after:bg-brand-primary after:w-1/2 after:scale-x-0 hover:after:scale-x-100 after:transition-transform
              ${isActive('/categoria/all')}`}
              >
              Categorias
              </Link>

              {/* CATEGORÍAS DINÁMICAS */}
              {categories.map((cat) => (
                <Link
                  key={cat.categoryId}
                  to={`/categoria/${cat.categoryId}`}
                  className={`flex-none w-[160px] text-center px-0 py-0 text-[12px] font-semibold uppercase tracking-tighter transition-all duration-300 hover:text-brand-primary relative
                  after:content-[''] after:absolute after:-bottom-1 after:left-1/4 after:h-[2px] after:bg-brand-primary after:w-1/2 after:scale-x-0 hover:after:scale-x-100 after:transition-transform
                  ${isActive(`/categoria/${cat.categoryId}`)}`}
                >
                  {cat.categoryName}
                </Link>
              ))}
            </div>

          </div>

          {/* FLECHA DERECHA SIEMPRE VISIBLE */}
          <button
            onClick={() => scroll("right")}
            className="z-10 bg-white border shadow-md rounded-full p-2  hover:bg-brand-primary hover:text-white transition-all active:scale-90 flex-shrink-0"
          >
            <IoChevronForward size={20} />
          </button>
        </div>

        {/* ORDER EXPRESS Y OTROS (Sin quitar nada) */}
        <div className="flex items-center gap-0">
          {user && (
            <Link
              to={isProforma ? "/order-express?mode=proforma" : "/order-express"}
              onClick={!user?.idCustomer ? (e) => e.preventDefault() : undefined}
              className={`
               justify-center px-2 py-2 rounded flex-shrink-0
              relative text-[12px] font-bold tracking-normal
              transition-colors
              after:content-[''] after:absolute after:-bottom-1 after:left-1/4
              after:h-[2px] after:w-1/2 after:bg-brand-primary
              after:scale-x-0 after:transition-transform
              ${!user?.idCustomer
                  ? "text-gray-400 pointer-events-none"
                  : "text-brand-primary/80 hover:text-brand-primary hover:after:scale-x-100"}
            `}
            >
              <span className="flex items-center">
                {isProforma ? "Solicitud de Proforma" : "Orden rápida"}
              </span>
            </Link>
          )}
          {user && (
            <Link
              to="/orders-review?doc_elect_sri=true"
              onClick={!user?.idCustomer ? (e) => e.preventDefault() : undefined}
              className={`
               justify-center px-2 py-2 rounded flex-shrink-0
              relative text-[12px] font-bold tracking-normal
              transition-colors
              after:content-[''] after:absolute after:-bottom-1 after:left-1/4
              after:h-[2px] after:w-1/2 after:bg-brand-primary
              after:scale-x-0 after:transition-transform
              ${!user?.idCustomer
                  ? "text-gray-400 pointer-events-none"
                  : "text-brand-primary/80 hover:text-brand-primary hover:after:scale-x-100"}
              `}
            >
              <span className="flex items-center">
                Documentos recibidos
              </span>
            </Link>
          )}

          {/* SWITCH ORDEN DE COMPRA / PROFORMA */}
          {user && (
            user.swWeb19? (
              <div className="flex-shrink-0 inline-flex flex-col rounded-lg border border-gray-300 overflow-hidden shadow-sm -mb-4 -mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsProforma(false);
                    localStorage.setItem("order_mode", "order");
                    window.dispatchEvent(new Event("orderModeChange"));
                  }}
                  className="px-3 py-1 text-[11px] font-bold tracking-tight transition-all duration-200"
                  style={{
                    backgroundColor: !isProforma ? "#80FF80" : "#f3f4f6",
                    color: !isProforma ? "#000000" : "#9ca3af",
                  }}
                >
                  Orden de Compra
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsProforma(true);
                    localStorage.setItem("order_mode", "proforma");
                    window.dispatchEvent(new Event("orderModeChange"));
                  }}
                  className="px-3 py-1 text-[11px] font-bold tracking-tight transition-all duration-200 border-t border-gray-300"
                  style={{
                    backgroundColor: isProforma ? "#FFFFB3" : "#f3f4f6",
                    color: isProforma ? "#000000" : "#9ca3af",
                  }}
                >
                  Solicitud de Proforma
                </button>
              </div>
            ) : (
              <div className="flex-shrink-0 inline-flex flex-col rounded-lg border border-gray-300 overflow-hidden shadow-sm -mb-4 -mt-4">
                <button
                  type="button"
                  disabled
                  className="px-3 py-1 text-[11px] font-bold tracking-tight"
                  style={{ backgroundColor: "#80FF80", color: "#000000" }}
                >
                  Orden de Compra
                </button>
              </div>
            )
          )}
          {/* MOBILE TOGGLE */}
          <div className="lg:hidden">
            <button onClick={toggleMenu} className="text-3xl text-ultra-red">
              {open ? <IoClose /> : <IoMenu />}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </nav>
  );
}